
import { Component, Vue } from "vue-property-decorator";
import vTable from "@/components/vTable.vue";
import { mapState } from "vuex";
import ListRequest, { Filter, Ordering, OrderType } from "@/models/ListRequest";
import { TRANSLATIONS_FETCH } from "@/store/ActionTypes";
import vSelect from "@/components/vSelect.vue";
import vModal from "@/components/vModal.vue";
import { NutrientSourceList } from "@/models/NutrientSource/NutrientSourceList";
import LocalizationTranslation from "@/models/Localization/ListTranslations";
import { AddTranslationKey } from "@/models/SystemConfiguration/AddTranslationKey";
import SystemConfigurationRepository from "@/utils/API/SystemConfigurationRepository";
import LocalizationRepository from "@/utils/API/LocalizationRepository";
import { UserTypes } from "../../models/User/UserTypes";
import { LocalizationLanguage } from "@/models/Localization/LocalizationLanguage";

//Types
type SelectObj = { id: number | undefined; name: string };
type LangSelectObj = {
  id: number | undefined;
  name: string;
  value: string;
  translation: string;
};
type CollectionSelectObj = {
  id: number | undefined;
  name: string;
  localizationCategoryId: number | undefined;
  localizationCategory: string | null;
};

interface SystemConfigurationState {
  nutrientSources: NutrientSourceList[];
  loading: boolean;
}

interface TranslationsGridState {
  translations: LocalizationTranslation[];
  loading: boolean;
  count: number;
  totalCount: number;
  pageSize: number;
  page: number;
}

interface Filters {
  categoryOptions: SelectObj[];
  collectionOptions: CollectionSelectObj[];
  languageOptions: LangSelectObj[];
  selectedLanguage: LangSelectObj | null | undefined;
  selectedCategory: SelectObj | null;
  selectedCollection: CollectionSelectObj | null;
}

@Component({
  components: {
    vTable,
    vSelect,
    vModal,
  },
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
      accountState: (state: any) => state.AccountModule,
    }),
  },
})
class SystemConfiguration extends Vue {
  /**
   * vTable props
   */
  selectErase = true;
  searchable = true;
  loading = false;
  removeList: number[] = []; // List of id of partners to be removed
  removePromptTitle = "";
  showRemovePrompt = false;
  filters: Filter[] = [];
  ordering: Ordering = { field: "", orderType: OrderType.Asc };
  editableTable = true;

  /**
   * Filter Properties
   */
  externalFiltersForGrid: Filters = {
    categoryOptions: [],
    collectionOptions: [],
    languageOptions: [],
    selectedLanguage: { id: undefined, name: "", value: "", translation: "" },
    selectedCategory: { id: undefined, name: "" },
    selectedCollection: {
      id: undefined,
      name: "",
      localizationCategoryId: undefined,
      localizationCategory: "",
    },
  };
  /**
   * Modal Properties
   */
  showAddNewLanguageModal = false;
  showAddNewTransKeyModal = false;
  showEditTranslationValueModal = false;

  /**
   * Edit translation value modal props
   */
  editTranslationValueProps: {
    newTranslationValue: string,
    translationId: number | null,
    languageId: number | null,
    editModalTitle: string
  } = {
    newTranslationValue: "",
    translationId: null,
    languageId: null,
    editModalTitle: "",
  };

  /**
   * Add new language modal props
   */
  newLanguage = {
    id: "",
    name: "",
  };
  allLanguageOptions: Omit<LocalizationLanguage, "value">[] = [];

  /**
   * Add new translation key modal props
   */
  categories: SelectObj[] = [];
  collections: SelectObj[] = [];
  addNewTranslationModal: AddTranslationKey = {
    category: { id: undefined, name: "" },
    collection: {
      id: undefined,
      name: "",
      localizationCategoryId: undefined,
      localizationCategory: "",
    },
    translationKey: "",
  };
  hideAddCategorySection: boolean = true;
  hideAddCollectionSection: boolean = true;
  newCategoryName: string = "";
  newCollectionName: string = "";

  /**
   * Translations grid props
   */
  translationsGridState: TranslationsGridState = {
    translations: [],
    loading: false,
    count: 0,
    totalCount: 0,
    pageSize: 25,
    page: 1,
  };

  localizationState: any;
  accountState: any;

  toggleableRows = true;

  systemConfigurationState: SystemConfigurationState = {
    nutrientSources: [],
    loading: false,
  };

  UserTypes = UserTypes;

  // START GRID PRIMARY FUNCTIONS

  selectAddNewLanguage(language: any) {
    this.newLanguage = language;
  }
  /**
   * Getter for grid headers. Done this way because headers are translatable and async
   */
  get tableHeadersForTranslations() {
    return [
      {
        name: "Category",
        key: "CategoryId",
        sortable: true,
        searchable: false,
        searchString: "",
      },
      {
        name: "Collection",
        key: "CollectionId",
        sortable: true,
        searchable: false,
        searchString: "",
      },
      {
        name: "Key",
        key: "LocalizationKey",
        searchable: true,
        searchString: "",
        sortable: true,
      },
      {
        name: this.externalFiltersForGrid.selectedLanguage!.value,
        key: "LanguageTranslationValue",
        searchable: true,
        searchString: "",
        sortable: true,
      },
    ];
  }

  get transformTranslations() {
    return this.translationsGridState.translations.map(
      (element: LocalizationTranslation) => {
        return {
          e1: element.localizationCollection.localizationCategory.name,
          e2: element.localizationCollection.name,
          e3: element.localizationKey.name,
          e4: element.value ? element.value : "",
          meta: {
            id: element.id,
          },
        };
      }
    );
  }

  /**
   * Method used by the grid after catching the 'changePage'
   * event from vTable
   *
   * @param {any} args
   */
  changePage(args: any) {
    const orderings: Ordering[] = [];
    orderings.push({ ...args[3] });
    this.fetchTranslations(
      new ListRequest({
        page: args[0],
        pageSize: 25,
        filters: args[2],
        orders: orderings,
      })
    );
  }

  /**
   * Method used by the grid after catching the 'search'
   * event from vTable
   *
   * @param {Filter[]} filters
   * @param {Ordering} ordering
   */
  search(filters: Filter[], ordering: Ordering) {
    // Add external filters to array, check for option 'show all' (id = -1)
    if (
      this.externalFiltersForGrid.selectedCategory!.id != undefined &&
      this.externalFiltersForGrid.selectedCategory!.id != -1
    ) {
      // If the filter is already in the array then just change the value, otherwise push it in the filters
      const index = filters.findIndex((obj) => obj.name === "CategoryId");
      if (index !== -1) {
        filters[index].value =
          this.externalFiltersForGrid.selectedCategory!.id.toString();
      }
      else {
        filters.push({
          name: "CategoryId",
          value: this.externalFiltersForGrid.selectedCategory!.id.toString(),
        });
      }
    }
    if (
      this.externalFiltersForGrid.selectedCollection!.id != undefined &&
      this.externalFiltersForGrid.selectedCollection!.id != -1
    ) {
      const index = filters.findIndex((obj) => obj.name === "CollectionId");
      if (index !== -1) {
        filters[index].value =
          this.externalFiltersForGrid.selectedCollection!.id.toString();
      }
      else {
        filters.push({
          name: "CollectionId",
          value: this.externalFiltersForGrid.selectedCollection!.id.toString(),
        });
      }
    }
    if (this.externalFiltersForGrid.selectedLanguage!.id != undefined) {
      const index = filters.findIndex((obj) => obj.name === "LanguageId");
      if (index !== -1) {
        filters[index].value =
          this.externalFiltersForGrid.selectedLanguage!.id.toString();
      }
      else {
        filters.push({
          name: "LanguageId",
          value: this.externalFiltersForGrid.selectedLanguage!.id.toString(),
        });
      }
    }
    const orderings: Ordering[] = [];
    orderings.push({ ...ordering });
    this.fetchTranslations(
      new ListRequest({
        page: 1,
        pageSize: this.translationsGridState.pageSize,
        filters,
        orders: orderings,
      })
    );
  }

  /**
   * Method for displaying prompt message for selected translations to be deleted
   *
   * @param {number[]} ids
   */
  prepareDeletePrompt(ids: number[]) {
    this.removeList = ids.map((id) => {
      const localizationTranslation =
        this.translationsGridState.translations.filter(
          (translation) => translation.id === id
        );
      return localizationTranslation[0].localizationKeyId;
    });
    this.removePromptTitle = this.translations.get(
      "sysconfig_delete_prompt_message",
      ids.length
    );
    this.showRemovePrompt = true;
  }

  /**
   * Populating form and toggling the modal for editing partner.
   * Sets the edit mode to true
   *
   * @param {number} id
   */
  prepareEditMode(id: number) {
    this.showEditTranslationValueModal = true;
    const translation: any = this.translationsGridState.translations.find(
      (translation: LocalizationTranslation) => translation.id === id
    );
    this.editTranslationValueProps.newTranslationValue = translation.value;
    this.editTranslationValueProps.translationId =
      translation.localizationKeyId;
    this.editTranslationValueProps.languageId =
      translation.localizationLanguageId;
    this.editTranslationValueProps.editModalTitle = this.translations.get(
      "partners_modal_title_edit"
    );
  }

  /**
   * Method used by the grid after catching the 'order'
   * event from vTable
   *
   * @param {Filter[]} filters
   * @param {Ordering} ordering
   */
  order(filters: Filter[], ordering: Ordering) {
    const orderings: Ordering[] = [];
    orderings.push({ ...ordering });
    this.fetchTranslations(
      new ListRequest({
        page: 1,
        pageSize: this.translationsGridState.pageSize,
        filters,
        orders: orderings,
      })
    );
  }

  // END GRID PRIMARY FUNCITONS

  fetchNutrientSources() {
    this.systemConfigurationState.loading = true;
    SystemConfigurationRepository.fetchNutrientSourcesList()
      .then((response) => {
        this.systemConfigurationState.nutrientSources = response.data.result;

        this.systemConfigurationState.loading = false;
      })
      .catch((error) => {
        if (error.response)
          this.$snotify.error(
            error.response.errorMessages[0].userVisibleMessage
          );
        else this.$snotify.error(this.translations.get("network-error"));
        this.systemConfigurationState.loading = false;
      });
  }

  get transformNutrients() {
    return this.systemConfigurationState.nutrientSources.map(
      (element: NutrientSourceList) => {
        return {
          e1: {
            component: "span",
            props: { id: element.nutrientSource },
            html: this.translations.get(
              `nutrient_source_${element.nutrientSource}`
            ),
          },
          meta: {
            id: element.nutrientSource,
            enabled: element.isEnabled,
          },
        };
      }
    );
  }

  get tableHeaders() {
    return [
      {
        name: this.translations.get("sysconfig_table_header_database"),
        key: "database",
        searchable: true,
        searchString: "",
        sortable: false,
      },
    ];
  }

  get linkToLabelsManager() {
    return `${process.env.VUE_APP_BASE_DOMAIN}/api/nutritionlabels/list`;
  }

  toggleSwitch({ value, id }: { value: boolean; id: number }) {
    let tmp = this.systemConfigurationState.nutrientSources.find(
      (source: NutrientSourceList) => source.nutrientSource === id
    );

    if (typeof tmp !== "undefined") {
      SystemConfigurationRepository.updateNutrientSource({
        nutrientSource: id,
        name: this.translations.get(`nutrient_source_${tmp.nutrientSource}`),
        isEnabled: value,
      }).catch((error) => {
        let message = "";
        if (error.response) {
          message = error.response.data.errorMessages[0].userVisibleMessage;
        }
        else {
          message = this.translations.get("network_error");
        }
        this.$snotify.error(message);
        this.fetchNutrientSources();
      });
    }
  }

  // Modal for adding a new language globally
  toggleAddNewLanguageModal() {
    this.showAddNewLanguageModal = true;
    this.fetchAllLanguagesForAddNewLanguage();
  }
  toggleAddNewTranslationKeyModal() {
    this.showAddNewTransKeyModal = true;
  }
  filterGridByCategory(category: SelectObj) {
    // Remove the filter for collection when you filter the grid by another category
    (this.externalFiltersForGrid.selectedCollection = {
      id: undefined,
      name: "",
      localizationCategoryId: undefined,
      localizationCategory: "",
    }),
    (this.externalFiltersForGrid.selectedCategory = category);

    // If show all is selected then don't fetch collections
    if (category.id !== -1) {
      this.fetchCollectionsForGrid(category.id);
    }
    else {
      this.externalFiltersForGrid.collectionOptions = [];
    }

    // Solves error 'Property does not exist on type vue'
    if (this.$refs.translationsGrid != undefined) {
      const translationsGrid = this.$refs.translationsGrid as vTable;
      translationsGrid.search();
    }
  }
  filterByCollection(collection: CollectionSelectObj) {
    this.externalFiltersForGrid.selectedCollection = collection;
    const translationsGrid = this.$refs.translationsGrid as vTable;
    translationsGrid.search();
  }
  filterByLanguage(language: LangSelectObj) {
    this.externalFiltersForGrid.selectedLanguage = language;
    const translationsGrid = this.$refs.translationsGrid as vTable;
    translationsGrid.search();
  }
  /**
   * Method that adds a new language globally in the whole application.
   */
  addNewLanguage() {
    // Validate fields before
    this.$validator.validateAll("addLanguage").then((result) => {
      if (result) {
        LocalizationRepository.createLocalizationLanguage(this.newLanguage.id)
          .then(() => {
            this.$snotify.success("New language added successfully.");
            this.resetState();
            this.fetchLanguages();
          })
          .catch((error) => {
            let message = "";
            if (error.response) {
              message = error.response.data.errorMessages[0].userVisibleMessage;
            }
            else {
              message = this.translations.get("network_error");
            }
            this.$snotify.error(message);
          });
        return;
      }
      this.$snotify.error(this.translations.get("validation-error-message"));
    });
  }
  /**
   * Method that fires when a user selects a category.
   */
  selectCategory(category: SelectObj) {
    this.addNewTranslationModal.category = category;
    this.addNewTranslationModal.collection = {
      id: undefined,
      name: "",
      localizationCategoryId: undefined,
      localizationCategory: "",
    };
    if (category.name === "Add new category") {
      // Show section when you choose correct option and reset collections
      this.hideAddCategorySection = false;
      this.collections = [{ id: -1, name: "Add new collection" }];
    }
    else if (!this.hideAddCategorySection) {
      // Else if the section is shown then hide it
      this.newCategoryName = "";
      this.hideAddCategorySection = true;
      this.fetchCollections(category.id);
    }
    else {
      // Else fetch collections for selected category
      this.fetchCollections(category.id);
    }
    // Clear validator because it will be triggered on collection reset
    this.errors.clear("addTransKey.collection");
    this.$nextTick(() => {
      this.$validator.reset();
    });
  }
  selectCollection(collection: CollectionSelectObj) {
    this.addNewTranslationModal.collection = collection;
    if (collection.name === "Add new collection") {
      // Show section when you choose correct option
      this.hideAddCollectionSection = false;
    }
    else if (!this.hideAddCollectionSection) {
      // Else if the section is shown then hide it
      this.newCollectionName = "";
      this.hideAddCollectionSection = true;
    }
  }
  /**
   * Method that adds a new category.
   */
  addNewCategory() {
    // Validate fields before
    this.$validator.validateAll("addNewCategory").then((result) => {
      if (result) {
        LocalizationRepository.createLocalizationCategory(this.newCategoryName)
          .then((response) => {
            if (response.data.result) {
              this.categories.push(response.data.result);
              this.closeAddCategorySection();
              this.$snotify.success("New category added successfully.");
            }
          })
          .catch((error) => {
            let message = "";
            if (error.response) {
              message = error.response.data.errorMessages[0].userVisibleMessage;
            }
            else {
              message = this.translations.get("network_error");
            }
            this.$snotify.error(message);
          });
        return;
      }
      this.$snotify.error(this.translations.get("validation-error-message"));
    });
  }
  /**
   * Method that adds a new collection.
   */
  addNewCollection() {
    // User first needs to select category for the collection
    if (
      this.addNewTranslationModal.category.id === -1 ||
      this.addNewTranslationModal.category.id === undefined
    ) {
      this.$snotify.info(
        "You need to select a category before adding the new collection."
      );
      return;
    }
    // Validate fields before
    this.$validator.validateAll("addNewCollection").then((result) => {
      if (result) {
        const model = {
          Name: this.newCollectionName,
          LocalizationCategoryId: this.addNewTranslationModal.category.id,
        };
        LocalizationRepository.createLocalizationCollection(model)
          .then((response) => {
            if (response.data.result) {
              // Push the new collection to array
              this.collections.push(response.data.result);
              this.closeAddCollectionSection();
              this.$snotify.success("New collection added successfully.");
            }
          })
          .catch((error) => {
            let message = "";
            if (error.response) {
              message = error.response.data.errorMessages[0].userVisibleMessage;
            }
            else {
              message = this.translations.get("network_error");
            }
            this.$snotify.error(message);
          });
        return;
      }
      this.$snotify.error(this.translations.get("validation-error-message"));
    });
  }
  closeAddCategorySection() {
    this.$set(this.addNewTranslationModal, "category", { id: null, name: "" });
    this.$nextTick(() => {
      this.errors.clear("addNewCategory");
      this.$nextTick(() => {
        this.$validator.reset();
      });
    });
    this.selectCategory(this.addNewTranslationModal.category);
  }
  closeAddCollectionSection() {
    this.$set(this.addNewTranslationModal, "collection", {
      id: null,
      name: "",
    });
    this.$nextTick(() => {
      this.errors.clear("addNewCollection");
      this.$nextTick(() => {
        this.$validator.reset();
      });
    });
    this.selectCollection(this.addNewTranslationModal.collection);
  }
  /**
   * Method that adds a new translation key and value globally in the whole application.
   */
  addNewTranslationKey() {
    // Validate fields before
    this.$validator.validateAll("addTransKey").then((result) => {
      if (result) {
        let keyLanguageValues: any = [];
        if (this.externalFiltersForGrid.languageOptions) {
          keyLanguageValues = this.externalFiltersForGrid.languageOptions.map(
            (language) => {
              return {
                LanguageId: language.id,
                Translation: language.translation ? language.translation : "",
              };
            }
          );
        }
        const model = {
          KeyLanguageValues: keyLanguageValues,
          CategoryId: this.addNewTranslationModal.category.id,
          CollectionId: this.addNewTranslationModal.collection.id,
          Key: this.addNewTranslationModal.translationKey,
        };
        LocalizationRepository.createTranslation(model)
          .then((response) => {
            if (response.data.result) {
              this.$snotify.success("New translation key added successfully.");
              this.resetState();
              this.fetchTranslations(
                new ListRequest({
                  page: this.translationsGridState.page,
                  pageSize: 25,
                  filters: this.filters,
                  orders: [this.ordering],
                })
              );
            }
          })
          .catch((error) => {
            let message = "";
            if (error.response) {
              message = error.response.data.errorMessages[0].userVisibleMessage;
            }
            else {
              message = this.translations.get("network_error");
            }
            this.$snotify.error(message);
          });
        return;
      }
      this.$snotify.error(this.translations.get("validation-error-message"));
    });
  }

  /**
   * Method for editing selected translation in the grid
   */
  editTranslationValue() {
    // @ts-ignore
    this.$snotify.async(
      this.translations.get("sysconfig_toastr_updating"),
      () =>
        new Promise((resolve, reject) => {
          const model = {
            KeyId: this.editTranslationValueProps.translationId,
            LanguageId: this.editTranslationValueProps.languageId,
            Value: this.editTranslationValueProps.newTranslationValue,
          };
          LocalizationRepository.updateTranslation(model)
            .then(() => {
              resolve({
                body: this.translations.get("sysconfig_toastr_updated"),
              });
              this.showEditTranslationValueModal = false;
              this.editTranslationValueProps = {
                newTranslationValue: "",
                translationId: null,
                languageId: null,
                editModalTitle: "",
              };

              if (
                this.externalFiltersForGrid.selectedCategory != null &&
                this.externalFiltersForGrid.selectedCategory.id != undefined
              ) {
                this.filters.push({
                  name: "CategoryId",
                  value:
                    this.externalFiltersForGrid.selectedCategory.id.toString(),
                });
              }

              if (
                this.externalFiltersForGrid.selectedCollection != null &&
                this.externalFiltersForGrid.selectedCollection.id != undefined
              ) {
                this.filters.push({
                  name: "CollectionId",
                  value:
                    this.externalFiltersForGrid.selectedCollection.id.toString(),
                });
              }

              this.fetchTranslations(
                new ListRequest({
                  page: this.translationsGridState.page,
                  pageSize: 25,
                  filters: this.filters,
                  orders: [this.ordering],
                })
              );
            })
            .catch((error: any) => {
              let message = "";
              if (error.response) {
                message =
                  error.response.data.errorMessages[0].userVisibleMessage;
              }
              else {
                message = this.translations.get("network_error");
              }
              this.resetState();
              reject({ body: message });
            });
        }),
      {
        timeout: 3000,
        closeOnClick: true,
      }
    );
  }

  /**
   * Method for deleting selected translation in the grid
   */
  confirmDelete() {
    // @ts-ignore
    this.$snotify.async(
      this.translations.get(
        "sysconfig_toastr_deleting",
        this.removeList.length
      ),
      () =>
        new Promise((resolve, reject) => {
          LocalizationRepository.deleteLocalizationKeys(this.removeList)
            .then(() => {
              resolve({
                body: this.translations.get(
                  "sysconfig_toastr_deleted",
                  this.removeList.length
                ),
              });
              this.resetState();
              this.resetExternalFilters();
              this.fetchTranslations(
                new ListRequest({
                  page: this.translationsGridState.page,
                  pageSize: 25,
                  filters: this.filters,
                  orders: [this.ordering],
                })
              );
              this.removeList = [];
            })
            .catch((error: any) => {
              let message = "";
              if (error.response) {
                message =
                  error.response.data.errorMessages[0].userVisibleMessage;
              }
              else {
                message = this.translations.get("network_error");
              }
              this.resetState();
              reject({ body: message });
            });
        }),
      {
        timeout: 3000,
        closeOnClick: true,
      }
    );
  }
  /**
   * Method that resets the values of the external filters.
   */
  resetExternalFilters() {
    this.externalFiltersForGrid.selectedLanguage = {
      id: undefined,
      name: "",
      value: "",
      translation: "",
    };
    this.externalFiltersForGrid.selectedCategory = { id: undefined, name: "" };
    this.externalFiltersForGrid.selectedCollection = {
      id: undefined,
      name: "",
      localizationCategoryId: undefined,
      localizationCategory: "",
    };
  }

  /**
   * Method that resets most variables and closes all modals.
   */
  resetState() {
    // Reset add new language modal state
    if (this.showAddNewLanguageModal) {
      this.showAddNewLanguageModal = false;
      this.newLanguage = {
        id: "",
        name: "",
      };
    }
    if (this.showAddNewTransKeyModal) {
      this.showAddNewTransKeyModal = false;
      // Clear all previous modal entries
      this.addNewTranslationModal = {
        category: { id: 3, name: "pages" },
        collection: {
          id: undefined,
          name: "",
          localizationCategoryId: undefined,
          localizationCategory: "",
        },
        translationKey: "",
      };
    }
    // Reset delete translation modal
    if (this.showRemovePrompt) {
      this.showRemovePrompt = false;
      this.removePromptTitle = "";
    }

    // Reset edit translation value modal props
    if (this.showEditTranslationValueModal) {
      this.showEditTranslationValueModal = false;
      this.editTranslationValueProps = {
        newTranslationValue: "",
        translationId: null,
        languageId: null,
        editModalTitle: "",
      };
    }
  }
  /**
   * Method that populates the grid with all translations available.
   */
  fetchTranslations(listRequest: ListRequest) {
    this.translationsGridState.loading = true;
    LocalizationRepository.listTranslations(listRequest)
      .then((response) => {
        this.translationsGridState.count = response.data.count;
        this.translationsGridState.totalCount = response.data.totalCount;
        this.translationsGridState.translations = response.data.result;
        this.translationsGridState.loading = false;
      })
      .catch((error) => {
        if (error.response)
          this.$snotify.error(
            error.response.errorMessages[0].userVisibleMessage
          );
        else this.$snotify.error(this.translations.get("network-error"));
        this.translationsGridState.loading = false;
      });
  }
  /**
   * Method that populates category filter options and modal for adding new translation key.
   */
  fetchCategories() {
    LocalizationRepository.getCategories()
      .then((response) => {
        // Populate collections in add new translation modal
        this.collections = [{ id: -1, name: "Add new collection" }];
        if (response.data.result && response.data.result.length) {
          this.categories = [
            { id: -1, name: "Add new category" },
            ...response.data.result,
          ];
          this.externalFiltersForGrid.categoryOptions = [
            { id: -1, name: "Show all" },
            ...response.data.result,
          ];
        }
        else {
          this.categories = [{ id: -1, name: "Add new category" }];
        }
      })
      .catch((error) => {
        let message = "";
        if (error.response) {
          message = error.response.data.errorMessages[0].userVisibleMessage;
        }
        else {
          message = this.translations.get("network_error");
        }
        this.$snotify.error(message);
      });
  }
  /**
   * Method that populates collection filter options and modal for adding new translation key, after the user chooses a category.
   */
  fetchCollections(categoryId: number | undefined) {
    if (categoryId) {
      LocalizationRepository.getCollections(categoryId)
        .then((response) => {
          if (response.data.result && response.data.result.length) {
            this.collections = [
              { id: -1, name: "Add new collection" },
              ...response.data.result,
            ];
          }
          else {
            this.collections = [{ id: -1, name: "Add new collection" }];
          }
        })
        .catch((error) => {
          let message = "";
          if (error.response) {
            message = error.response.data.errorMessages[0].userVisibleMessage;
          }
          else {
            message = this.translations.get("network_error");
          }
          this.$snotify.error(message);
        });
    }
  }
  fetchCollectionsForGrid(categoryId: number | undefined) {
    LocalizationRepository.getCollections(categoryId)
      .then((response) => {
        if (response.data.result && response.data.result.length) {
          this.externalFiltersForGrid.collectionOptions = [
            { id: -1, name: "Show all" },
            ...response.data.result,
          ];
        }
      })
      .catch((error) => {
        let message = "";
        if (error.response) {
          message = error.response.data.errorMessages[0].userVisibleMessage;
        }
        else {
          message = this.translations.get("network_error");
        }
        this.$snotify.error(message);
      });
  }
  /**
   * Method that populates language filter options.
   */
  fetchLanguages() {
    LocalizationRepository.getLanguages()
      .then((response) => {
        if (response.data.result && response.data.result.length) {
          this.externalFiltersForGrid.languageOptions = response.data.result;
          // On initial load show english translations
          this.externalFiltersForGrid.selectedLanguage =
            this.externalFiltersForGrid.languageOptions.find(
              (element) => element.id === 1
            );
        }
      })
      .catch((error) => {
        let message = "";
        if (error.response) {
          message = error.response.data.errorMessages[0].userVisibleMessage;
        }
        else {
          message = this.translations.get("network_error");
        }
        this.$snotify.error(message);
      });
  }

  /**
   * Method that populates language filter options.
   */
  fetchAllLanguagesForAddNewLanguage() {
    LocalizationRepository.getAvailableLanguagesForAdding()
      .then((response) => {
        if (response && response.length) {
          this.allLanguageOptions = response.map((x: any) => {
            return {
              id: x.name,
              name: x.value,
            };
          });
        }
      })
      .catch((error) => {
        let message = "";
        if (error.response) {
          message = error.response.data.errorMessages[0].userVisibleMessage;
        }
        else {
          message = this.translations.get("network_error");
        }
        this.$snotify.error(message);
      });
  }

  mounted() {
    this.fetchCategories();
    this.fetchLanguages();
    // Filter grid by english language on first load
    this.filters.push({ name: "LanguageId", value: "1" });
    this.filters.push({ name: "CategoryId", value: "3" });
    this.fetchTranslations(
      new ListRequest({
        page: this.translationsGridState.page,
        pageSize: 25,
        filters: this.filters,
        orders: [this.ordering],
      })
    );
  }
  created() {
    if (this.accountState.userDetails.userType === UserTypes.SuperAdministrator)
      this.fetchNutrientSources();

    this.$store.dispatch(TRANSLATIONS_FETCH, {
      category: "pages",
      collections: ["sysconfig", "common"],
    });
    this.filterGridByCategory({ id: 3, name: "pages" });
    document.title = "System Configuration | MenuSano";
  }

  get translations() {
    return this.localizationState.translations;
  }
}
export default SystemConfiguration;
